// packages/front/src/portfolio/modals/import-transactions-modal.tsx
//
// CSV Import modal for transactions — Stage 16.
// Upload CSV → preview rows → map columns → submit to bulk import endpoint.
// REQ-PORTFOLIO-16.

import { useCallback, useMemo, useState, type FC, type FormEvent } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Modal, Select, SmartTable } from "../../_common";
import { INCOME_EVENT_TYPE_OBJ, TRANSACTION_TYPE_OBJ } from "@signals/shared";
import { useBulkImportTransactions } from "../portfolio.hooks";

export interface ImportTransactionsModalProps {
  open: boolean;
  onClose: () => void;
  portfolioId: string;
}

/** Transaction fields that a CSV column can be mapped to. */
type TxField =
  | "accountName"
  | "securityTicker"
  | "date"
  | "type"
  | "quantity"
  | "price"
  | "commission"
  | "currency"
  | "note";

const TX_FIELDS: TxField[] = [
  "accountName",
  "securityTicker",
  "date",
  "type",
  "quantity",
  "price",
  "commission",
  "currency",
  "note",
];

function resolveBulkType(value: string | undefined): "buy" | "sell" | "dividend" {
  if (value === TRANSACTION_TYPE_OBJ.SELL || value === INCOME_EVENT_TYPE_OBJ.DIVIDEND) return value;
  return "buy";
}

/** Header-name → field auto-detection map (case-insensitive substring match). */
const HEADER_HINTS: Array<{ field: TxField; patterns: string[] }> = [
  { field: "accountName", patterns: ["account", "accountname", "acct"] },
  { field: "securityTicker", patterns: ["ticker", "symbol", "security", "securityticker"] },
  { field: "date", patterns: ["date", "trade date", "tradedate"] },
  { field: "type", patterns: ["type", "action", "side", "transaction type"] },
  { field: "quantity", patterns: ["qty", "quantity", "shares", "units"] },
  { field: "price", patterns: ["price", "fill price", "unit price"] },
  { field: "commission", patterns: ["commission", "fee", "fees"] },
  { field: "currency", patterns: ["currency", "ccy"] },
  { field: "note", patterns: ["note", "notes", "memo", "comment", "description"] },
];

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const labelClass = "text-sm font-medium";

interface BulkImportResponse {
  created: number;
  updated: number;
  imported: number;
  total: number;
  errors: Array<{ row: number; error: string }>;
}

interface PreviewTableRow {
  id: string;
  [key: string]: string;
}

/**
 * Minimal RFC 4180 CSV parser — handles quoted fields, escaped quotes,
 * and CRLF/LF line endings. No external dependency (DRY: no CSV lib in repo).
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const ch = text[i]!;

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }

    if (ch === ",") {
      row.push(field);
      field = "";
      i++;
      continue;
    }

    if (ch === "\r") {
      // CRLF — consume \r, let \n terminate the row
      i++;
      continue;
    }

    if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      i++;
      continue;
    }

    field += ch;
    i++;
  }

  // Flush trailing field/row (file without final newline)
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Drop fully-empty trailing rows
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

function autoDetectField(header: string): TxField | "" {
  const h = header.trim().toLowerCase();
  if (!h) return "";
  for (const { field, patterns } of HEADER_HINTS) {
    if (patterns.some((p) => h === p || h.includes(p))) {
      return field;
    }
  }
  return "";
}

function buildExternalId(row: Record<string, string>, index: number): string {
  const parts = [
    row["accountName"] ?? "",
    row["securityTicker"] ?? "",
    row["date"] ?? "",
    row["type"] ?? "",
    row["quantity"] ?? "",
    row["price"] ?? "",
  ];
  const joined = parts.join("|").trim();
  return joined ? `csv-${joined}-${index}` : `csv-row-${index}`;
}

export const ImportTransactionsModal: FC<ImportTransactionsModalProps> = ({
  open,
  onClose,
  portfolioId,
}) => {
  const bulkImport = useBulkImportTransactions(portfolioId);

  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [dataRows, setDataRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<number, TxField | "">>({});
  const [result, setResult] = useState<BulkImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setFileName("");
    setHeaders([]);
    setDataRows([]);
    setMapping({});
    setResult(null);
    setError(null);
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      setResult(null);
      setError(null);
      setFileName(file.name);
      try {
        const text = await file.text();
        const rows = parseCsv(text);
        if (rows.length === 0) {
          setError("CSV file is empty");
          setHeaders([]);
          setDataRows([]);
          return;
        }
        const hdrs = rows[0]!.map((h) => h.trim());
        const body = rows.slice(1);
        setHeaders(hdrs);
        setDataRows(body);
        // Auto-detect mapping from header names
        const auto: Record<number, TxField | ""> = {};
        hdrs.forEach((h, idx) => {
          auto[idx] = autoDetectField(h);
        });
        setMapping(auto);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to parse CSV");
      }
    },
    [],
  );

  const previewRows = useMemo(() => dataRows.slice(0, 8), [dataRows]);
  const previewTableData = useMemo<PreviewTableRow[]>(
    () =>
      previewRows.map((row, rowIndex) => ({
        id: String(rowIndex),
        ...Object.fromEntries(
          headers.map((_, columnIndex) => [
            `column-${columnIndex}`,
            row[columnIndex] ?? "",
          ]),
        ),
      })),
    [headers, previewRows],
  );
  const previewColumns: ColumnDef<(typeof previewTableData)[number]>[] =
    headers.map((header, columnIndex) => ({
      id: `column-${columnIndex}`,
      accessorFn: (row) => row[`column-${columnIndex}`],
      header: () => (
        <span>
          {header}
          {mapping[columnIndex] && (
            <span className="ml-1 text-[10px] text-blue-600">
              → {mapping[columnIndex]}
            </span>
          )}
        </span>
      ),
    }));

  const mappedRows = useMemo(() => {
    if (headers.length === 0) return [];
    return dataRows.map((row, rowIdx) => {
      const obj: Record<string, string> = {};
      headers.forEach((_h, colIdx) => {
        const field = mapping[colIdx];
        if (field) {
          obj[field] = (row[colIdx] ?? "").trim();
        }
      });
      // externalId is required by backend but not user-mapped — auto-generate
      obj["externalId"] = buildExternalId(obj, rowIdx);
      return obj;
    });
  }, [dataRows, headers, mapping]);

  const canSubmit =
    headers.length > 0 &&
    dataRows.length > 0 &&
    Object.values(mapping).some((v) => v !== "");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      const rows = mappedRows.map((r) => ({
        accountName: r["accountName"] ?? "",
        securityTicker: r["securityTicker"] ?? "",
        externalId: r["externalId"] ?? "",
        type: resolveBulkType(r["type"]),
        quantity: Number(r["quantity"]) || 0,
        price: Number(r["price"]) || 0,
        currency: r["currency"] || "USD",
        date: r["date"] || new Date().toISOString().slice(0, 10),
        ...(r["commission"] ? { commission: Number(r["commission"]) || 0 } : {}),
        ...(r["note"] ? { note: r["note"] } : {}),
        source: "import",
      }));
      const res = await bulkImport.mutateAsync(rows);
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Import Transactions (CSV)"
      maxWidth="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Step 1: File upload */}
        <div className="flex flex-col gap-2">
          <label className={labelClass}>CSV File</label>
          <input
            type="file"
            accept=".csv,text/csv"
            className={inputClass}
            data-testid="transaction-csv-file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          {fileName && (
            <p className="text-xs text-muted-foreground">
              Loaded: {fileName} ({dataRows.length} rows)
            </p>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {/* Step 2: Preview + column mapping */}
        {headers.length > 0 && (
          <>
            <div>
              <h3 className={labelClass}>Column Mapping</h3>
              <p className="text-xs text-muted-foreground mb-2">
                Map each CSV column to a transaction field. Auto-detected from
                header names — adjust as needed.
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {headers.map((h, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="w-32 truncate text-xs text-muted-foreground" title={h}>
                      {h}
                    </span>
                    <Select
                      value={mapping[idx] ?? ""}
                      onChange={(value) => {
                        const selectedField = TX_FIELDS.find(
                          (field) => field === value,
                        );
                        setMapping((prev) => ({
                          ...prev,
                          [idx]: selectedField ?? "",
                        }));
                      }}
                      options={[
                        { value: "", label: "— ignore —" },
                        ...TX_FIELDS.map((f) => ({ value: f, label: f })),
                      ]}
                      ariaLabel={`Map CSV column ${h}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className={labelClass}>Preview (first {previewRows.length} rows)</h3>
              <SmartTable
                columns={previewColumns}
                data={previewTableData}
                total={previewTableData.length}
                page={1}
                pageSize={Math.max(previewTableData.length, 1)}
                isLoading={false}
                error={null}
                filters={[]}
                sort={[]}
                onPageChange={() => {}}
                onPageSizeChange={() => {}}
                onFiltersChange={() => {}}
                onSortChange={() => {}}
                rowIdField="id"
              />
              {dataRows.length > previewRows.length && (
                <p className="text-xs text-muted-foreground mt-1">
                  …and {dataRows.length - previewRows.length} more rows
                </p>
              )}
            </div>
          </>
        )}

        {/* Step 3: Result */}
        {result && (
          <div className="rounded-md border border-input bg-muted/30 p-3 text-sm">
            <p className="font-medium">
              Imported: {result.imported} / {result.total}
              {result.created > 0 && ` (created: ${result.created})`}
              {result.updated > 0 && ` (updated: ${result.updated})`}
            </p>
            {result.errors.length > 0 && (
              <div className="mt-2">
                <p className="text-red-600 font-medium">
                  Errors ({result.errors.length}):
                </p>
                <ul className="mt-1 max-h-32 overflow-auto text-xs text-red-600">
                  {result.errors.map((err, i) => (
                    <li key={i}>
                      Row {err.row}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            {result ? "Close" : "Cancel"}
          </Button>
          {headers.length > 0 && !result && (
            <Button type="button" variant="outline" onClick={reset}>
              Reset
            </Button>
          )}
          {headers.length > 0 && !result && (
            <Button type="submit" disabled={!canSubmit || bulkImport.isPending}>
              {bulkImport.isPending ? "Importing..." : `Import ${dataRows.length} rows`}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};
